<template>
  <button 
    type="button"
    class="workflow-button flex justify-center items-center"
    :class="[
      type, 
      computedColorClass, 
      fontWeight,
      { 'is-shadow': isShadow }, 
      { disabled },
      { 'disabled-fill': disabledFill },
      { 'disabled-stroke': disabledStroke },
      { loading },
      { 'large-font': largeFont }
    ]"
    :disabled="disabled"
    :style="computedButtonStyle"
    @click="handleClick"
  >
    <div v-if="prefixUrl" class="prefix-icon">
      <img :src="require(`@/assets/${prefixUrl}`)" :class="loading ? 'loading-icon' : ''" />
    </div>
    <slot></slot>
    <div v-if="suffixUrl" class="suffix-icon">
      <img :src="require(`@/assets/${suffixUrl}`)" :class="loading ? 'loading-icon' : ''" />
    </div>
  </button>
</template>
  
<script>
export default {
  name: "WorkflowButton",
  props: {
    type: {
      type: String,
      default: "normal" // normal, narrow, round, custom
    },
    // Button style: stroke or fill
    buttonStyle: {
      type: String,
      default: "fill" // fill, stroke, none-stroke
    },
    // Color type: dark-blue, sky-blue, red
    colorType: {
      type: String,
      default: "dark-blue" // dark-blue, sky-blue, red
    },
    // Custom color (overrides colorType if provided)
    customColor: {
      type: String,
      default: null // e.g., "#131234"
    },
    // Custom height prop
    height: {
      type: [String, Number],
      default: null // e.g., "40px", "32px", 40
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: null,
    }, 
    hasShadow: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    largeFont: {
      type: Boolean,
      default: false,
    },
    isBold: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["click"],
  data() {
    return {
    };
  },
  computed: {
    computedButtonStyle() {
      let styles = {};
      
      // Apply custom height
      if (this.height) {
        const heightValue = typeof this.height === 'number' ? `${this.height}px` : this.height;
        styles.height = heightValue;
      }
      
      // Apply custom color if provided
      if (this.customColor) {
        if (this.buttonStyle === 'fill') {
          styles.backgroundColor = this.customColor;
          styles.color = '#FFFFFF';
          styles.border = 'none';
        } else if (this.buttonStyle === 'stroke') {
          styles.backgroundColor = '#FFFFFF';
          styles.color = this.customColor;
          styles.border = `1px solid ${this.customColor}`;
        }
      }
      
      return styles;
    },
    computedColorClass() {
      // If custom color is provided, return 'custom'
      if (this.customColor) {
        return `custom-${this.buttonStyle}`;
      }
      
      // Otherwise use colorType with buttonStyle
      if (this.buttonStyle === 'stroke') {
        return `${this.colorType}-stroke`;
      } else if (this.buttonStyle === 'none-stroke') {
        return 'none-stroke';
      }
      return this.colorType;
    },
    isShadow() {
      if (this.buttonStyle === "none-stroke") {
        return false;
      }
      return this.hasShadow;
    },
    fontWeight() {
      if (this.isBold) {
        return "heavy-weight";
      }
      if (this.buttonStyle === "stroke" || (this.customColor && this.buttonStyle === "stroke")) {
        return "light-weight";
      } 
      return "normal-weight";
    },
    disabledFill() {
      return this.disabled && (this.buttonStyle === "fill" || (!this.customColor && ["dark-blue", "sky-blue", "red"].includes(this.colorType)));
    },
    disabledStroke() {
      return this.disabled && this.buttonStyle === "stroke";
    },
    loadingUrl() {
      if (this.buttonStyle === "fill") {
        return "NewLibrary/loading_light.svg";
      } else if (this.colorType === "red" && this.buttonStyle === "stroke") {
        return "NewLibrary/loading_red.svg";
      }
      return "NewLibrary/loading_dark.svg";
    },
    prefixUrl() {
      if (!this.loading) {
        return this.prefix;
      } else {
        if (!this.suffix) {
          return this.loadingUrl;
        } else {
          return "";
        }
      }
    },
    suffixUrl() {
      if (!this.loading) {
        return this.suffix;
      } else {
        if (this.suffix) {
          return this.loadingUrl;
        } else {
          return "";
        }
      }
    }
  },
  methods: {
    handleClick(e) {
      if (this.loading) {
        e.stopPropagation();
        return;
      } else {
        this.$emit("click");
      }
    }
  },
};
</script>
  
<style scoped>
.workflow-button {
  font-size: 14px;
  font-family: "IBM Plex Sans Hebrew";
  line-height: 20px;
  letter-spacing: 0.25px;
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
  cursor: pointer;
}

.normal {
  height: 40px;
  padding: 8px 32px;
  border-radius: 4px;
}

.narrow {
  height: 38px;
  padding: 8px 20px;
  border-radius: 4px;
}

.round {
  height: 32px;
  padding: 4px 32px;
  border-radius: 15px;
}

.custom {
  padding: 8px 32px;
  border-radius: 4px;
}

.heavy-weight {
  font-style: normal;
  font-weight: 600;
}

.normal-weight {
  font-style: normal;
  font-weight: 500;
}

.light-weight {
  font-style: normal;
  font-weight: 400;
}

.dark-blue {
  background: var(--VibrantDarkBlue, #004879);
  color: #FFF;
  border: none;
}

.sky-blue {
  background: var(--New_Button_SkyBlue, #81B0E1);
  color: #FFF;
  border: none;
}

.red {
  background: var(--ErrorRed, #D10000);
  color: #FFF;
  border: none;
}

.none-stroke {
  background: none;
  color: var(--color-Darkblue-000, #1E4670);
  padding: 4px 10px;  
  line-height: 14px;
  height: 26px;
  text-decoration-line: underline;
  border: none;
}

.dark-blue-stroke {
  border: 1px solid var(--TextButton_Blue, #1E4670);
  background: none;
  color: var(--color-Darkblue-000, #1E4670);
}

.sky-blue-stroke {
  border: 1px solid var(--New_Button_SkyBlue, #81B0E1);
  background: none;
  color: var(--New_Library_PressBlue, #5587BC);
}

.red-stroke {
  border: 1px solid var(--ErrorRed, #D10000);
  background: none;
  color: var(--ErrorRed, #D10000);
}

/* Hover states */
.dark-blue:hover, .sky-blue:hover {
  background: #20A7C1;
}

.dark-blue:active, .sky-blue:active {
  background: #5587BC;
}

.red:hover, .red-stroke:hover {
  background: var(--ErrorRed_Hover, #F63B3B);
  color: #FFF;
}

.red:active, .red-stroke:active {
  background: #A60B0B;
  color: #FFF;
}

.dark-blue-stroke:hover {
  background: rgba(30, 70, 112, 0.08);
}

.dark-blue-stroke:active {
  background: rgba(30, 70, 112, 0.12);
}

.sky-blue-stroke:hover {
  background: #FDF9F6;
}

.sky-blue-stroke:active {
  background: #EDF1F4;
}

.none-stroke:hover {
  background: rgba(30, 70, 112, 0.08);
}

.none-stroke:active {
  background: rgba(30, 70, 112, 0.12);
}

/* Custom color hover states */
.custom-fill:hover:not(:disabled) {
  filter: brightness(1.1);
}

.custom-fill:active:not(:disabled) {
  filter: brightness(0.9);
}

.custom-stroke:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.04);
}

.custom-stroke:active:not(:disabled) {
  background: rgba(0, 0, 0, 0.08);
}

/* Disabled states */
.none-stroke:disabled, .disabled.none-stroke:hover, .disabled.none-stroke:active {
  color: var(--DividerGray, #C4C4C4);
  background: none;
}

.disabled {
  cursor: not-allowed;
}

.disabled-fill, .disabled-fill:hover, .disabled:active {
  background: var(--DividerGray, #C4C4C4);
  color: #FFF;
}

.disabled-stroke, .disabled-stroke:hover, .disabled-stroke:active {
  border: 1px solid var(--ContentMediumGrey, #999);
  background: #FFF;
  color: var(--ContentMediumGrey, #999);
}

.loading-icon {
  animation: spin 2s linear infinite;
}

.large-font {
  font-size: 18px;
}

.is-shadow {
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
}

.prefix-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 4px;
  flex-shrink: 0;
}

.prefix-icon img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

.suffix-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 4px;
  flex-shrink: 0;
}

.suffix-icon img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
